import BackendService from 'BackendService';
import { useCallback, useEffect, useState } from 'react';
import { buildChatPrefsPayload, mapChatPrefsFromResponse } from './chatNotificationUtils';
import {
  CHAT_OAUTH_START_FAILED_MESSAGE,
  ChatNotificationPreferences,
  defaultChatPrefs,
} from './settingsTypes';

interface UseChatNotificationPreferencesOptions {
  onLoadError: (message: string) => void;
  onSaveSuccess: (message: string) => void;
}

export function useChatNotificationPreferences({
  onLoadError,
  onSaveSuccess,
}: UseChatNotificationPreferencesOptions) {
  const [chatPrefs, setChatPrefs] = useState<ChatNotificationPreferences>(defaultChatPrefs);
  const [chatPrefsLoading, setChatPrefsLoading] = useState(true);
  const [chatPrefsSaving, setChatPrefsSaving] = useState(false);
  const [chatPrefsHasChanges, setChatPrefsHasChanges] = useState(false);
  const [googleChatConnecting, setGoogleChatConnecting] = useState(false);
  const [googleChatDisconnecting, setGoogleChatDisconnecting] = useState(false);
  const [teamsChatConnecting, setTeamsChatConnecting] = useState(false);
  const [teamsChatDisconnecting, setTeamsChatDisconnecting] = useState(false);
  const [slackChatConnecting, setSlackChatConnecting] = useState(false);
  const [slackChatDisconnecting, setSlackChatDisconnecting] = useState(false);
  const [addChannelMenuAnchor, setAddChannelMenuAnchor] = useState<HTMLElement | null>(null);

  const fetchChatNotificationPreferences = useCallback(async () => {
    try {
      setChatPrefsLoading(true);
      const response = await new BackendService().getChatNotificationPreferences();
      setChatPrefs(mapChatPrefsFromResponse(response.data));
      setChatPrefsHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch chat notification preferences:', error);
      onLoadError(
        error instanceof Error ? error.message : 'Failed to load chat notification preferences',
      );
    } finally {
      setChatPrefsLoading(false);
    }
  }, [onLoadError]);

  useEffect(() => {
    void fetchChatNotificationPreferences();
  }, [fetchChatNotificationPreferences]);

  const updateChatPrefs = (patch: Partial<ChatNotificationPreferences>) => {
    setChatPrefs((prev) => ({ ...prev, ...patch }));
    setChatPrefsHasChanges(true);
  };

  const handleSaveChatPrefs = async () => {
    try {
      setChatPrefsSaving(true);
      onLoadError('');
      await new BackendService().saveChatNotificationPreferences(buildChatPrefsPayload(chatPrefs));
      onSaveSuccess('Chat timing saved.');
      setChatPrefsHasChanges(false);
      await fetchChatNotificationPreferences();
    } catch (error) {
      onLoadError(error instanceof Error ? error.message : 'Failed to save chat timing');
      await fetchChatNotificationPreferences();
    } finally {
      setChatPrefsSaving(false);
    }
  };

  const handleAddEmail = async () => {
    setAddChannelMenuAnchor(null);
    try {
      setChatPrefsSaving(true);
      await new BackendService().saveChatNotificationPreferences(
        buildChatPrefsPayload(chatPrefs, { emailEnabled: true }),
      );
      await fetchChatNotificationPreferences();
    } catch (error) {
      onLoadError(error instanceof Error ? error.message : 'Failed to add email channel');
    } finally {
      setChatPrefsSaving(false);
    }
  };

  const handleRemoveEmail = async () => {
    try {
      setChatPrefsSaving(true);
      await new BackendService().saveChatNotificationPreferences(
        buildChatPrefsPayload(chatPrefs, { emailEnabled: false }),
      );
      await fetchChatNotificationPreferences();
    } catch (error) {
      onLoadError(error instanceof Error ? error.message : 'Failed to remove email channel');
    } finally {
      setChatPrefsSaving(false);
    }
  };

  const handleAddGoogleChat = async () => {
    setAddChannelMenuAnchor(null);
    try {
      setGoogleChatConnecting(true);
      onLoadError('');
      const response = await new BackendService().getGoogleChatConnectUrl();
      window.location.href = response.data.url;
    } catch {
      onLoadError(CHAT_OAUTH_START_FAILED_MESSAGE.google);
      setGoogleChatConnecting(false);
    }
  };

  const handleRemoveGoogleChat = async () => {
    try {
      setGoogleChatDisconnecting(true);
      onLoadError('');
      await new BackendService().disconnectGoogleChat();
      await fetchChatNotificationPreferences();
    } catch (error) {
      onLoadError(error instanceof Error ? error.message : 'Failed to remove Google Chat');
    } finally {
      setGoogleChatDisconnecting(false);
    }
  };

  const handleAddTeamsChat = async () => {
    setAddChannelMenuAnchor(null);
    try {
      setTeamsChatConnecting(true);
      onLoadError('');
      const response = await new BackendService().getTeamsChatConnectUrl();
      window.location.href = response.data.url;
    } catch {
      onLoadError(CHAT_OAUTH_START_FAILED_MESSAGE.teams);
      setTeamsChatConnecting(false);
    }
  };

  const handleRemoveTeamsChat = async () => {
    try {
      setTeamsChatDisconnecting(true);
      onLoadError('');
      await new BackendService().disconnectTeamsChat();
      await fetchChatNotificationPreferences();
    } catch (error) {
      onLoadError(error instanceof Error ? error.message : 'Failed to remove Microsoft Teams');
    } finally {
      setTeamsChatDisconnecting(false);
    }
  };

  const handleAddSlackChat = async () => {
    setAddChannelMenuAnchor(null);
    try {
      setSlackChatConnecting(true);
      onLoadError('');
      const response = await new BackendService().getSlackChatConnectUrl();
      window.location.href = response.data.url;
    } catch {
      onLoadError(CHAT_OAUTH_START_FAILED_MESSAGE.slack);
      setSlackChatConnecting(false);
    }
  };

  const handleRemoveSlackChat = async () => {
    try {
      setSlackChatDisconnecting(true);
      onLoadError('');
      await new BackendService().disconnectSlackChat();
      await fetchChatNotificationPreferences();
    } catch (error) {
      onLoadError(error instanceof Error ? error.message : 'Failed to remove Slack');
    } finally {
      setSlackChatDisconnecting(false);
    }
  };

  return {
    chatPrefs,
    setChatPrefs,
    chatPrefsLoading,
    chatPrefsSaving,
    chatPrefsHasChanges,
    googleChatConnecting,
    googleChatDisconnecting,
    teamsChatConnecting,
    teamsChatDisconnecting,
    slackChatConnecting,
    slackChatDisconnecting,
    addChannelMenuAnchor,
    setAddChannelMenuAnchor,
    fetchChatNotificationPreferences,
    updateChatPrefs,
    handleSaveChatPrefs,
    handleAddEmail,
    handleRemoveEmail,
    handleAddGoogleChat,
    handleRemoveGoogleChat,
    handleAddTeamsChat,
    handleRemoveTeamsChat,
    handleAddSlackChat,
    handleRemoveSlackChat,
  };
}
